import { fetchPushVapidPublicKey, subscribeWebPush, unsubscribeWebPush } from "../api/client";
import { WEB_PUSH_ENDPOINT_STORAGE_KEY } from "./storageKeys";
import { readTabBearerToken } from "../utils/tabAuthStorage";

export function isWebPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    typeof Notification !== "undefined"
  );
}

export function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/** Rótulo amigável para listagem de dispositivos (como Google Chat). */
export function detectDeviceLabel() {
  if (typeof navigator === "undefined") return "Navegador";
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const standalone =
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true);

  if (isIOS) {
    if (/iPad/i.test(ua)) return standalone ? "iPad (app instalado)" : "iPad";
    return standalone ? "iPhone (app instalado)" : "iPhone";
  }
  if (isAndroid) return standalone ? "Android (app instalado)" : "Android";
  if (/Windows/i.test(ua)) return "Windows";
  if (/Macintosh|Mac OS X/i.test(ua)) return "macOS";
  if (/Linux/i.test(ua)) return "Linux";
  if (/CrOS/i.test(ua)) return "Chromebook";
  return "Navegador";
}

export function readStoredPushEndpoint() {
  try {
    return localStorage.getItem(WEB_PUSH_ENDPOINT_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

export function storePushEndpoint(endpoint) {
  try {
    if (endpoint) localStorage.setItem(WEB_PUSH_ENDPOINT_STORAGE_KEY, String(endpoint));
    else localStorage.removeItem(WEB_PUSH_ENDPOINT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

async function resolvePushRegistration() {
  let reg = await navigator.serviceWorker.getRegistration("/sw-push.js");
  if (!reg) {
    reg = await navigator.serviceWorker.register("/sw-push.js");
  }
  return reg;
}

export async function getLocalPushSubscription() {
  if (!isWebPushSupported()) return null;
  try {
    const reg = await resolvePushRegistration();
    return reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Garante subscription local + registro no servidor.
 * @returns {Promise<{ subscription: PushSubscription, serverId: number|null }>}
 */
export async function registerWebPushOnDevice({ requestPermission = false } = {}) {
  if (!isWebPushSupported()) {
    throw new Error("unsupported");
  }

  if (requestPermission && Notification.permission !== "granted") {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") throw new Error("permission_denied");
  }

  if (Notification.permission !== "granted") {
    throw new Error("permission_not_granted");
  }

  const reg = await resolvePushRegistration();
  await navigator.serviceWorker.ready;

  const publicKey = await fetchPushVapidPublicKey();
  if (!publicKey) throw new Error("no_vapid");

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const payload = sub.toJSON ? sub.toJSON() : sub;
  const res = await subscribeWebPush(payload, {
    deviceLabel: detectDeviceLabel(),
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent || "" : "",
  });

  const serverId =
    res?.data?.id != null
      ? Number(res.data.id)
      : res?.id != null
        ? Number(res.id)
        : null;

  if (sub?.endpoint) storePushEndpoint(sub.endpoint);

  return { subscription: sub, serverId: Number.isFinite(serverId) ? serverId : null };
}

/** Registro silencioso após login (sem pedir permissão). */
export async function autoRegisterWebPushIfPermitted() {
  if (!isWebPushSupported()) return null;
  if (Notification.permission !== "granted") return null;
  try {
    return await registerWebPushOnDevice({ requestPermission: false });
  } catch {
    return null;
  }
}

/** Remove inscrição deste aparelho no servidor e no navegador. */
export async function unregisterLocalWebPush() {
  if (!isWebPushSupported()) return;
  const sub = await getLocalPushSubscription();
  const endpoint = sub?.endpoint || readStoredPushEndpoint();

  if (endpoint) {
    try {
      const token = readTabBearerToken();
      const { getApiBaseURL } = await import("../api/apiBaseUrl");
      const baseURL = getApiBaseURL();
      if (token && baseURL) {
        let company = null;
        try {
          const raw = localStorage.getItem("company");
          company = raw ? String(raw).trim() : null;
        } catch {
          /* ignore */
        }
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Xpro-Client": "browser",
        };
        if (company) headers.Company = company;
        await fetch(`${baseURL}/push/unsubscribe`, {
          method: "POST",
          keepalive: true,
          headers,
          body: JSON.stringify({ endpoint }),
        });
      }
    } catch {
      /* token pode já ter sido removido */
    }
  }

  try {
    if (sub) await sub.unsubscribe();
  } catch {
    /* ignore */
  }

  storePushEndpoint("");
}

export async function removeRemoteWebPushDevice({ id, endpoint }) {
  await unsubscribeWebPush({ id, endpoint });
  const localEndpoint = readStoredPushEndpoint();
  if (endpoint && localEndpoint && localEndpoint === endpoint) {
    try {
      const sub = await getLocalPushSubscription();
      if (sub) await sub.unsubscribe();
    } catch {
      /* ignore */
    }
    storePushEndpoint("");
  }
}

export function formatDeviceLastSeen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export function resolveDeviceDisplayLabel(row) {
  if (row?.device_label) return String(row.device_label);
  const ua = row?.user_agent ? String(row.user_agent) : "";
  if (/iPhone/i.test(ua)) return "iPhone";
  if (/iPad/i.test(ua)) return "iPad";
  if (/Android/i.test(ua)) return "Android";
  if (/Windows/i.test(ua)) return "Windows";
  if (/Macintosh|Mac OS X/i.test(ua)) return "macOS";
  if (row?.endpoint && String(row.endpoint).includes("web.push.apple.com")) return "Apple";
  if (row?.endpoint && String(row.endpoint).includes("fcm.googleapis.com")) return "Chrome / Android";
  return "Dispositivo";
}
